import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

import subscriptionPaymentService from "../../api/subscriptionPaymentService";
import { getTrackingData } from "../../utils/trackingConfig";
import usePlan from "../../hooks/usePlan";
import {
  activityStart,
  activityEnd,
} from "../../redux/slices/trackActivitySlice";
import usePageTracking from "../../hooks/usePageTracking";

import "./Trial.scss";

/* /trial — entry page for the paid Pro Trial.
 * POST /frontend/start-trial creates a Stripe Checkout session
 * (Pro Yearly + 3-day trial + $2.99 one-time fee) and returns its URL.
 * We redirect the user there; on success they land on /success. */

const ClockIcon = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...p}
  >
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

const LockIcon = (p) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...p}
  >
    <rect x="4.5" y="11" width="15" height="9" rx="2" />
    <path d="M8 11V7.5a4 4 0 0 1 8 0V11" />
  </svg>
);

const makeSteps = (billingCycle) => [
  {
    n: 1,
    title: "Today",
    body: (
      <>
        <strong>No charge.</strong> Full Pro access starts immediately.
      </>
    ),
  },
  {
    n: 2,
    title: "Day 3 reminder",
    body:
      "We'll send you a reminder before your free trial ends. Cancel anytime in Settings.",
  },
  {
    n: 3,
    title: "Day 4",
    body: (
      <>
        <strong>
          {billingCycle === "month"
            ? "$2.99/month charged"
            : "$29.99/year charged"}
        </strong>{" "}
        automatically to your payment method.{" "}
        {billingCycle === "month"
          ? "Renews monthly unless canceled."
          : "Renews annually unless canceled."}
      </>
    ),
  },
];

const Trial = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const billingCycle = location.state?.billingCycle || "year";
  const triggerSource = location.state?.source || "direct";
  const { trigger } = getTrackingData(triggerSource);
  const { isTrialEligible } = usePlan();
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    dispatch(activityStart({ page_name: "TRIAL", platform: "web" }));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(activityEnd({ page_name: "TRIAL" }));
    };
  }, [dispatch]);

  usePageTracking("TRIAL");

  // Users who've already claimed the trial (or any paid plan) shouldn't see
  // this page. Send them to subscription management instead.
  if (!isTrialEligible) {
    return (
      <div className="mmp-trial">
        <div className="trial-card trial-card--simple">
          <h2 className="trial-headline">You've already claimed your trial</h2>
          <p className="trial-sub">
            Subscribe to Pro for $29.99/year or pick another plan from the
            subscription page.
          </p>
          <button
            type="button"
            className="trial-btn trial-btn--primary"
            onClick={() => navigate("/profile/subscription")}
          >
            See plans
          </button>
        </div>
      </div>
    );
  }

  const handleStartTrial = async () => {
    setIsProcessing(true);
    try {
      const response = await subscriptionPaymentService.startTrial({ trigger_feature: trigger });
      const url = response?.data?.url;
      if (!url) {
        toast.error("Couldn't start trial. Please try again.");
        setIsProcessing(false);
        return;
      }
      // Stripe Checkout is hosted — bounce the browser to it.
      window.location.href = url;
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        "Couldn't start trial. Please try again.";
      toast.error(message);
      setIsProcessing(false);
    }
  };

  return (
    <div className="mmp-trial">
      <div className="trial-card">
        <div className="trial-icon-tile" aria-hidden="true">
          <ClockIcon />
        </div>

        <h1 className="trial-headline">Here's what happens</h1>
        <p className="trial-sub">3-day free trial of MapMyPay Pro</p>

        <ol className="trial-steps">
          {makeSteps(billingCycle).map((s) => (
            <li key={s.n} className="trial-step">
              <span className="trial-step-num">{s.n}</span>
              <div className="trial-step-body">
                <div className="trial-step-title">{s.title}</div>
                <div className="trial-step-text">{s.body}</div>
              </div>
            </li>
          ))}
        </ol>

        <div className="trial-info">
          <span className="trial-info-icon" aria-hidden="true">
            <LockIcon />
          </span>
          <p>
            If you cancel before day 4, you'll keep Pro access through the end
            of your 3-day trial and won't be charged.
          </p>
        </div>

        <button
          type="button"
          className="trial-btn trial-btn--primary"
          onClick={handleStartTrial}
          disabled={isProcessing}
        >
          {isProcessing ? "Starting…" : "Continue to payment"}
        </button>

        <button
          type="button"
          className="trial-btn trial-btn--text"
          onClick={() => navigate("/profile/subscription")}
          disabled={isProcessing}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

export default Trial;
